import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { countPostChars, socialCharLimit, socialPlatformLabel } from '@mg/shared';
import { useProxyImage } from '../../lib/useProxyImage';
import { useFieldId } from '../../components/ui/useFieldId';
import type { PostContext } from './postContext';

/**
 * The post a Generate window was opened from, at the top of that window.
 *
 * Collapsed to its opening lines by default, because the form below it is the work and a 3,000
 * character post would push it off screen. "Show post" opens the whole text, so someone writing a
 * prompt can check a detail without leaving the window.
 */
export function PostContextBand({ context, title }: { context: PostContext; title: string }) {
  const [expanded, setExpanded] = useState(false);
  const bodyId = useFieldId('post-context');
  const limit = socialCharLimit(context.platform);
  const source = [
    context.platform ? socialPlatformLabel[context.platform] : null,
    context.accountLabel?.trim() || null,
  ]
    .filter(Boolean)
    .join(' · ');
  const text = context.text.trim();

  return (
    <section aria-label={title} className="border-b border-line-soft bg-page px-5 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-strong">{title}</p>
          {source && <p className="text-xs text-ink-muted">{source}</p>}
        </div>
        <button
          type="button"
          className="focus-ring inline-flex min-h-11 shrink-0 items-center gap-1 rounded-lg px-2 text-sm font-medium text-ink-label hover:bg-hover"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Hide post' : 'Show post'}
          <ChevronDown
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>

      <div id={bodyId}>
        {!text ? (
          <p className="mt-1 text-sm text-ink-muted">This post has no text yet.</p>
        ) : expanded ? (
          <div className="mt-2 max-h-[30vh] space-y-2 overflow-y-auto pr-1 text-sm text-ink">
            {text.split(/\n+/).map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        ) : (
          <p className="mt-1 line-clamp-2 text-sm text-ink">{text.replace(/\s+/g, ' ')}</p>
        )}

        {expanded && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <MediaStrip media={context.media} />
            {limit != null && (
              <p className="text-xs text-ink-muted">
                {countPostChars(context.platform, context.text).toLocaleString('en-US')} /{' '}
                {limit.toLocaleString('en-US')} characters
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function MediaStrip({ media }: { media: PostContext['media'] }) {
  if (media.length === 0) return <p className="text-xs text-ink-muted">No media attached yet</p>;
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Media on this post">
      {media.slice(0, 6).map((m) => (
        <li key={m.id}>
          <MediaThumb url={m.url} />
        </li>
      ))}
      {media.length > 6 && (
        <li className="flex h-10 items-center px-1 text-xs text-ink-muted">+{media.length - 6}</li>
      )}
    </ul>
  );
}

function MediaThumb({ url }: { url: string }) {
  const src = useProxyImage(url);
  return (
    <span className="block h-10 w-10 overflow-hidden rounded-md border border-line bg-card">
      {src && <img src={src} alt="" className="h-full w-full object-cover" />}
    </span>
  );
}
